import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import type { SVGProps } from 'react';
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Sidebar } from './sidebar';
import { useT } from '../i18n/locale-context';

function MenuIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path strokeLinecap="round" d="M3 5.5h14M3 10h14M3 14.5h14" />
    </svg>
  );
}

function CloseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path strokeLinecap="round" d="M5 5l10 10M15 5L5 15" />
    </svg>
  );
}

/**
 * Mobile/tablet navigation: a hamburger button (shown below `lg`, where the
 * persistent sidebar is hidden) that opens the full nav as a left off-canvas
 * drawer. Reuses the same `<Sidebar />` so nav logic stays single-sourced.
 *
 * Radix Dialog powers the sheet: focus is trapped + restored, Esc and overlay
 * clicks close it, and the trigger gets `aria-expanded`/`aria-controls` for
 * free. The drawer additionally closes on route change (an in-drawer nav tap)
 * via `useLocation`. SSR-safe: open state lives in `useState`, no window access
 * at render, and the portal only mounts once open.
 */
export function MobileNav() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close the drawer whenever the route changes — e.g. a nav link inside the
  // drawer was tapped. Runs in an effect (client-only), never at render.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label={t('shell.nav.openMenu')}
          className="-ml-1.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-ink-soft transition-colors hover:bg-bone hover:text-ink lg:hidden"
        >
          <MenuIcon className="h-[22px] w-[22px]" />
        </button>
      </SheetTrigger>

      <SheetContent side="left" className="p-0">
        {/* Accessible name for the dialog (Radix requires a Title). */}
        <SheetTitle className="sr-only">{t('shell.nav.menu')}</SheetTitle>
        <SheetDescription className="sr-only">{t('shell.nav.menu.a11yDesc')}</SheetDescription>

        <SheetClose
          aria-label={t('shell.nav.closeMenu')}
          className="absolute right-2.5 top-3.5 z-20 flex h-9 w-9 items-center justify-center rounded-md bg-bone-surface text-ink-muted transition-colors hover:bg-bone hover:text-ink"
        >
          <CloseIcon className="h-5 w-5" />
        </SheetClose>

        <Sidebar className="h-full w-full border-r-0" />
      </SheetContent>
    </Sheet>
  );
}
